import type { RequestResponse } from '@/types/publicType'
import type {
  roomInfoInterface,
  membersInterface,
  clientConfigureInterface,
  detonatingGiftInterface,
  activityQueryInterface,
  luckyBagInterface
} from '~/types/apiType/roomType'

import type { trtcTokenInterface } from '@/types/apiType/roomType'
import type { ROOM_TYPE } from '#imports'

/**
 * 获取房间信息
 * @param {number} unionid
 */
export function apiGetRoomInfo(params: {
  roomId: number | string
}): Promise<RequestResponse<roomInfoInterface>> {
  return getFetchData({
    url: '/h5/r/entry',
    method: 'GET',
    params
  })
}

/**
 * 获取房间在线成员列表
 * @param {number} unionid
 */
export function apiGetMemberList(data: {
  room_id: number | string
  start: number
  limit: number
}): Promise<RequestResponse<membersInterface>> {
  return getFetchData({
    url: '/h5/i/members',
    method: 'POST',
    data
  })
}

/**
 * 锁麦/开麦操作
 * @param {number} position 麦序位置
 * @param {number} roomUid 房主UID
 * @param {number} state 状态，1：锁麦，0开麦（即取消锁麦）
 * @param {number} type 房间类型
 */
export function apiLockMic(data: {
  position: number
  roomUid: number
  state: number
  type: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/r/lockmic',
    method: 'POST',
    data
  })
}

/**
 * 锁坑位/取消锁
 * @param {number} position 麦序位置
 * @param {number} roomUid 房主UID
 * @param {number} state 状态 1：锁坑位，0取消锁
 */
export function apiLockPos(params: {
  position: number
  roomUid: number
  state: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/r/lockpos',
    method: 'GET',
    params
  })
}

/**
 * 锁坑位/取消锁
 * @param {number} position 麦序位置
 * @param {number} roomUid 房UID
 * @param {number} state 状态 1：锁坑位，0取消锁
 */
export function apiLockPosBatch(params: {
  roomUid: number
  state: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/r/lockPosBatch',
    method: 'GET',
    params
  })
}

/**
 * 踢出房间
 * @param {number} roomId 房间id
 * @param {number} targetUid 目标用户uid
 * @param {number} timeUnit 封禁单位,1-天，2-小时
 * @param {number} kickOutDay 对应的时长
 */
export function apikikUser(params: {
  roomId: number
  targetUid: number
  timeUnit?: number
  kickOutDay?: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/r/kick/user',
    method: 'GET',
    params
  })
}

/**
 * 获取房间SDK签名参数
 * @param {number} roomId 房间id
 * @param {number} type 固定让后端返回渠道 1.声网
 */
export function apiGetSDKToken(data: {
  roomId: number
  type?: number
}): Promise<RequestResponse<trtcTokenInterface>> {
  return getFetchData({
    url: '/inner/im/getEnterRoomTrtcToken',
    method: 'POST',
    data
  })
}

/**
 * 获取SDK房间上麦签名参数
 * @param {number} roomId 房间id
 */
export function apiGetMinSDKToken(data: {
  roomId: number
}): Promise<RequestResponse<trtcTokenInterface>> {
  return getFetchData({
    url: '/inner/im/getUpMicTrtcToken',
    method: 'POST',
    data
  })
}

/**
 * 更新主播的直播时长统计状态
 * @param {number} roomId 房间id
 * @param {1|2} playTimeStatus 主播直播时长统计状态：1-暂停；2-恢复
 */
export function apiUpdateAnchorPlayTimeStatus(
  params: {
    roomId: number
    playTimeStatus: 1 | 2
  },
  loading: boolean
): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/room/updateAnchorPlayTimeStatus',
    method: 'GET',
    params,
    loading
  })
}

/**
 * 获取房间游戏列表配置
 */
export function apiGetClientConfigure(): Promise<
  RequestResponse<clientConfigureInterface>
> {
  return getFetchData({
    url: '/client/configure',
    method: 'GET'
  })
}

/**
 * 设置管理员
 */
export function apiSetManager(data: {
  room_id: number
  account: number
  is_add: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/imroom/v1/markChatRoomManager',
    method: 'POST',
    data
  })
}

/**
 * 更新房间信息(房主用)
 */
export function apiUpdateRoomInfo(data: {
  roomType: number | string
  roomUid: number | string
  publicChatSwitch?: 1 | 0
  showOtherEnterAnimSwitch?: 1 | 0
}): Promise<RequestResponse<roomInfoInterface>> {
  return getFetchData({
    url: '/room/update',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 更新房间信息(管理员用)
 */
export function apiUpdateRoomInfoByAdmin(data: {
  roomType: number | string
  roomUid: number | string
  publicChatSwitch?: 1 | 0
  showOtherEnterAnimSwitch?: 1 | 0
}): Promise<RequestResponse<roomInfoInterface>> {
  return getFetchData({
    url: '/room/updateByAdmin',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 清空房间魅力值
 */
export function apiResetRoomCharm(data: {
  roomId: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/userroom/resetRoomCharm',
    method: 'POST',
    data
  })
}

/**
 * 抢取爆炸礼物
 * @param data.roomId
 */
export function apiGetDetonatingGift(data: {
  roomId: number
}): Promise<RequestResponse<detonatingGiftInterface>> {
  return getFetchData({
    url: '/room/getDetonatingGift',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 房间活动角标列表
 * @param params.type 1.首页，2.直播间
 */
export function apiActivityQuery(params: {
  type: number
}): Promise<RequestResponse<activityQueryInterface>> {
  return getFetchData({
    url: '/activity/query',
    method: 'GET',
    params
  })
}

/**
 * 房间幸运红包
 */
export function apiLuckyBag(params: {
  roomId: number
}): Promise<RequestResponse<luckyBagInterface | null>> {
  return getFetchData({
    url: '/room/lucky/bag/get',
    method: 'GET',
    params
  })
}

/**
 * 记录进入房间
 */
export function apiRoomIn(data: {
  roomUid: number
  roomType: ROOM_TYPE
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/userroom/in',
    method: 'POST',
    data
  })
}

/**
 * 记录退出房间
 */
export function apiRoomOut(data: {
  roomUid: number
  roomType: ROOM_TYPE
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/userroom/out',
    method: 'POST',
    data
  })
}

/**
 * 退出视频房间(主播才调用)
 */
export function apiRoomEndLive(params: {
  roomUid: number
  roomType: ROOM_TYPE
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/room/endLive',
    method: 'GET',
    params
  })
}
